import {useCallback, useEffect, useMemo, useState} from 'react'
import {useDispatch, useSelector} from 'react-redux'
import {useHistory} from 'react-router'
import {parse} from 'qs'
import {includes} from 'ramda'
import {toggle} from 'opticks/lib/optimizely'
import {
  ActionCreatorWithOptionalPayload,
  createSelector
} from '@reduxjs/toolkit'

import {setIsAuthenticated, setUser, showPasswordlessLogin} from './slice'
import {BrandType} from '../../brand/types'
import {
  getCognitoUserTierFromCookie,
  getLastLoginUrl,
  removeCognitoUserTierFromCookie,
  removeUserLastConnectionMethodCookie,
  setLastLoginUrl
} from '../business/persistData'

import {
  AuthenticationFlows,
  ShowPasswordlessLoginPayload,
  UserAction
} from '../types/Auth'

import {UserType} from '../../user/types'
import {User} from '../types/Cognito'
import {
  hasAudienceTokenInUrl,
  hasExpectedGsiteValueInUrl,
  USER_TIERS
} from '../../offer/business/privateDeals'

import {
  getUserLastCheckoutDate,
  getUserProfilePicture,
  getUserTier,
  hasPlusTier,
  selectCognitoUserTier,
  selectEnabledBrands,
  selectIsAuthenticated,
  selectIsLoading,
  selectUser
} from './selectors'
import {signOutService} from '../services'
import {getUrlPathName} from '../utils/url'

interface MetaState {
  brand: BrandType
}

interface State {
  meta: MetaState
}

interface AuthConnection {
  domain: string
  clientId: string
  audience: string
}

export interface UseCognitoType {
  authEnabled: boolean
  isAuthenticated: boolean
  isMemberPlus: boolean
  isEmployee: boolean
  lastCheckoutDate: string
  isPrivileged: boolean
  isAudience: boolean
  isLoading: boolean
  logIn: (arg0: ShowPasswordlessLoginPayload) => void
  logOut: () => Promise<void>
  user: Partial<UserType>
  accountUrl: string
  setUser: ActionCreatorWithOptionalPayload<UserAction>
  authConnection: AuthConnection
  loginPathName: string
}

const getBrand = (state: State) => state.meta.brand
const selectBrand = createSelector(getBrand, brand => brand)

export const getIsEmployee = (user?: Partial<UserType>) => {
  const userTierFromCookie = getCognitoUserTierFromCookie()
  const userTierFromMetaData = (user && getUserTier(user)) || []
  const userTier =
    userTierFromCookie.length > 0 ? userTierFromCookie : userTierFromMetaData

  return (
    includes(USER_TIERS.employee, userTier) ||
    includes(USER_TIERS.friend, userTier)
  )
}

export const getIsMemberPlus = () =>
  hasPlusTier(getCognitoUserTierFromCookie()) || false

const handleLogIn = ({
  sourceComponent,
  flow,
  anonymousId
}: ShowPasswordlessLoginPayload) => {
  const isSmsFlowEnabled = toggle('aded6ba5-sms-flow-enabled', false, true)
  const authFlow =
    flow === AuthenticationFlows.UnlockOverlay && !isSmsFlowEnabled
      ? AuthenticationFlows.SignUp
      : flow

  return showPasswordlessLogin({
    anonymousId,
    sourceComponent,
    flow: authFlow || AuthenticationFlows.Login
  })
}

enum Connections {
  google = 'google-oauth2',
  facebook = 'facebook',
  sms = 'sms'
}

export const getConnection = (user?: User) => {
  if (user?.attributes?.identities) {
    const identities = JSON.parse(user?.attributes?.identities)
    const providerName = identities?.[0]?.providerName
    return providerName === 'Google' ? Connections.google : Connections.facebook
  }

  return Connections.sms
}

export const mapCognitoUserToAuth0User = (user?: User): Partial<UserType> => ({
  id: user?.id,
  sub: user?.attributes?.sub,
  name: user?.attributes?.given_name,
  phoneNumber: user?.attributes?.phone_number,
  picture: user?.attributes?.picture,
  email: user?.attributes?.email,
  email_verified: user?.attributes?.email_verified,
  updated_at: user?.updatedAt,
  'https://findhotel.net/user_id': user?.id,
  'https://findhotel.net/createdAt': user?.createdAt,
  'https://findhotel.net/connection': getConnection(user)
})

export const useCognitoUser = () => {
  const cognitoUser = useSelector(selectUser)
  const picture = useSelector(getUserProfilePicture)
  return useMemo(
    () => cognitoUser && {...mapCognitoUserToAuth0User(cognitoUser), picture},
    [cognitoUser, picture]
  )
}

export const useIsEmployee = () => {
  const cognitoUser = useCognitoUser()
  return getIsEmployee(cognitoUser)
}

export const useIsAudience = () => {
  const history = useHistory()
  const urlParams = useMemo(
    () =>
      parse(history?.location?.search, {
        ignoreQueryPrefix: true
      }),
    [history?.location?.search]
  )

  return useMemo(() => hasAudienceTokenInUrl(urlParams), [urlParams?.token])
}

export const useIsMemberPlus = () => {
  const userTier = useSelector(selectCognitoUserTier)
  return useMemo(() => hasPlusTier(userTier) || getIsMemberPlus(), [userTier])
}

export const useIsPrivileged = () => {
  const history = useHistory()
  const isAuthenticated = useSelector(selectIsAuthenticated)
  const urlParams = useMemo(
    () =>
      parse(history?.location?.search, {
        ignoreQueryPrefix: true
      }),
    [history?.location?.search]
  )

  const isAudience = useIsAudience()

  return useMemo(
    () =>
      isAuthenticated || isAudience || hasExpectedGsiteValueInUrl(urlParams),
    [isAuthenticated, isAudience]
  )
}

export const useAuthEnabled = () => {
  const enabledBrands = useSelector(selectEnabledBrands)
  const brand = useSelector(selectBrand)

  return useMemo(
    () => enabledBrands.includes(brand?.code),
    [enabledBrands, brand?.code]
  )
}

export const useAuthConnection = () =>
  useMemo(
    () => ({
      clientId: null,
      audience: null,
      domain: null
    }),
    []
  )

export const useAccountUrl = () => {
  const brand = useSelector(selectBrand)
  return useMemo(() => `https://${brand.defaultDomain}/user/account`, [brand])
}

export const useLogIn = () => {
  const dispatch = useDispatch()

  return useCallback(({sourceComponent, flow, anonymousId}) => {
    dispatch(
      handleLogIn({
        sourceComponent,
        flow,
        anonymousId
      })
    )
  }, [])
}

export const useLogOut = () => {
  const dispatch = useDispatch()

  return useCallback(async () => {
    setLastLoginUrl(window.location.href)
    removeCognitoUserTierFromCookie()
    removeUserLastConnectionMethodCookie()

    dispatch(setIsAuthenticated({isAuthenticated: false}))

    await signOutService()
  }, [])
}

export const useCognito = (): UseCognitoType => {
  const isAuthenticated = useSelector(selectIsAuthenticated)
  const isLoading = useSelector(selectIsLoading)
  const loginPathName = getUrlPathName(getLastLoginUrl())
  const [lastCheckoutDate, setLastCheckoutDate] = useState<string>()

  const user = useCognitoUser()
  const authEnabled = useAuthEnabled()
  const isAudience = useIsAudience()
  const isEmployee = useIsEmployee()
  const isPrivileged = useIsPrivileged()
  const isMemberPlus = useIsMemberPlus()
  const authConnection = useAuthConnection()
  const accountUrl = useAccountUrl()

  const logIn = useLogIn()
  const logOut = useLogOut()

  useEffect(() => {
    if (isAuthenticated) {
      setLastCheckoutDate(getUserLastCheckoutDate(user))
    }
  }, [isAuthenticated])

  return {
    authEnabled,
    isAuthenticated,
    isPrivileged,
    isAudience,
    user,
    isMemberPlus,
    isEmployee,
    lastCheckoutDate,
    setUser,
    isLoading,
    logIn,
    logOut,
    loginPathName,
    accountUrl,
    authConnection
  }
}
